import React, { useState, useEffect } from 'react';
import { api, formatCurrency } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Users,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Receipt,
  BarChart3,
  FileText,
  PiggyBank,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';

const CHART_COLORS = ['#18181B', '#52525B', '#A1A1AA', '#22C55E', '#EF4444', '#3B82F6'];

export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role || 'staff';
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [marginAlerts, setMarginAlerts] = useState([]);
  const [recentTxns, setRecentTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const promises = [
        api.get('/dashboard/summary'),
        api.get('/dashboard/charts'),
      ];
      if (role !== 'accountant') {
        promises.push(api.get('/ingredients/margin-radar').catch(() => ({ data: [] })));
      }
      // Fetch recent transactions for staff view
      if (role === 'staff') {
        promises.push(api.get('/revenues').catch(() => ({ data: [] })));
        promises.push(api.get('/expenses').catch(() => ({ data: [] })));
      }
      const results = await Promise.all(promises);
      setSummary(results[0].data);
      setChartData(results[1].data);
      if (role !== 'accountant') setMarginAlerts(results[2]?.data || []);
      if (role === 'staff' && results[3] && results[4]) {
        const revs = (results[3].data || []).map(r => ({ ...r, _type: 'revenue' }));
        const exps = (results[4].data || []).map(e => ({ ...e, _type: 'expense' }));
        const all = [...revs, ...exps].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 10);
        setRecentTxns(all);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  const expenseChartData = summary?.expense_breakdown 
    ? Object.entries(summary.expense_breakdown).map(([name, value]) => ({
        name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), value
      })) : [];

  const ventureChartData = summary?.venture_revenue
    ? Object.entries(summary.venture_revenue).map(([name, value]) => ({ name, value })) : [];

  // Role-specific subtitle
  const subtitle = role === 'staff' ? "Today's operations at a glance" 
    : role === 'accountant' ? 'Financial analysis overview' 
    : "Here's your financial snapshot";

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="dashboard">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">
            Good {getGreeting()}, {user?.name?.split(' ')[0]}
          </h1>
          <span className="px-2 py-1 text-xs font-medium bg-gradient-to-r from-amber-100 to-amber-50 text-amber-700 rounded-full border border-amber-200 capitalize">
            {role}
          </span>
        </div>
        <p className="text-zinc-500 mt-1">{subtitle}</p>
      </div>

      {role === 'staff' ? <StaffDashboard summary={summary} marginAlerts={marginAlerts} recentTxns={recentTxns} /> 
       : role === 'accountant' ? <AccountantDashboard summary={summary} chartData={chartData} expenseChartData={expenseChartData} ventureChartData={ventureChartData} /> 
       : <OwnerDashboard summary={summary} chartData={chartData} marginAlerts={marginAlerts} expenseChartData={expenseChartData} ventureChartData={ventureChartData} />}
    </div>
  );
}

// ========== STAFF DASHBOARD ==========
function StaffDashboard({ summary, marginAlerts, recentTxns }) {
  return (
    <div className="bento-grid stagger-children" data-testid="staff-dashboard">
      <div className="bento-span-4">
        <MetricCard icon={TrendingUp} iconBg="from-amber-400 to-amber-500" label="Today's Revenue" value={formatCurrency(summary?.sales_this_month || 0)} sub={`${summary?.sales_change_percent >= 0 ? '+' : ''}${summary?.sales_change_percent || 0}% vs last month`} />
      </div>
      <div className="bento-span-4">
        <MetricCard icon={TrendingDown} iconBg="from-zinc-400 to-zinc-500" label="Today's Expenses" value={formatCurrency(summary?.expenses_this_month || 0)} sub="Operating expenses" />
      </div>

      {/* Recent Transactions */}
      <div className="bento-span-8">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2"><Receipt className="w-4 h-4" />Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            {recentTxns.length === 0 ? (
              <div className="text-center py-8"><p className="text-sm text-zinc-500">No recent transactions</p></div>
            ) : (
              <div className="space-y-2">
                {recentTxns.map((tx, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-50 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tx._type === 'revenue' ? 'bg-green-50' : 'bg-red-50'}`}>
                        {tx._type === 'revenue' ? <ArrowUpRight className="w-4 h-4 text-green-600" /> : <ArrowDownRight className="w-4 h-4 text-red-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-800">{tx.description || tx.category || 'Transaction'}</p>
                        <p className="text-xs text-zinc-400">{tx.date}</p>
                      </div>
                    </div>
                    <span className={`text-sm font-semibold ${tx._type === 'revenue' ? 'text-green-600' : 'text-red-600'}`}>
                      {tx._type === 'revenue' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Margin Alerts */}
      <div className="bento-span-4">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Margin Radar</CardTitle>
          </CardHeader>
          <CardContent>
            {marginAlerts.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center mx-auto mb-3"><TrendingUp className="w-5 h-5 text-zinc-400" /></div>
                <p className="text-sm text-zinc-500">No alerts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {marginAlerts.slice(0, 5).map((alert, i) => (
                  <div key={i} className={`alert-card ${alert.direction === 'up' ? 'warning' : 'info'}`}>
                    <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${alert.direction === 'up' ? 'text-amber-500' : 'text-blue-500'}`} />
                    <div className="text-sm">{alert.type === 'price_change' ? <><span className="font-medium">{alert.ingredient}</span> price {alert.direction === 'up' ? 'increased' : 'decreased'} <span className={alert.direction === 'up' ? 'text-amber-600' : 'text-green-600'}>{Math.abs(alert.change_percent)}%</span></> : alert.message}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ========== ACCOUNTANT DASHBOARD ==========
function AccountantDashboard({ summary, chartData, expenseChartData, ventureChartData }) {
  const navigate = useNavigate();
  const profitMargin = summary?.sales_this_month > 0 
    ? ((summary.operating_profit / summary.sales_this_month) * 100).toFixed(1) : 0;
  const hasPendingBankTx = (summary?.pending_bank_tx_count || 0) > 0;

  return (
    <div className="bento-grid stagger-children" data-testid="accountant-dashboard">
      {/* Pending Bank Transactions Warning */}
      {hasPendingBankTx && (
        <div className="col-span-12">
          <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="pending-bank-warning">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-800">
                  {summary.pending_bank_tx_count} bank transactions pending confirmation
                </p>
                <p className="text-sm text-amber-600">
                  Pending expense amount: {formatCurrency(summary.pending_bank_expense_amount || 0)} — These won't appear in reports until confirmed
                </p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/exception-review')}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
            >
              Review & Confirm <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Financial Metrics - 5 columns */}
      <div className="col-span-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <MetricCard icon={TrendingUp} iconBg="from-amber-400 to-amber-500" label="Revenue" value={formatCurrency(summary?.sales_this_month || 0)} sub={`${summary?.sales_change_percent >= 0 ? '+' : ''}${summary?.sales_change_percent || 0}% vs last month`} />
        <MetricCard icon={TrendingDown} iconBg="from-zinc-400 to-zinc-500" label="Operating Expenses" value={formatCurrency(summary?.expenses_this_month || 0)} sub="Excl. investments" />
        <Card className={`roch-card metric-card h-full ${(summary?.operating_profit || 0) >= 0 ? 'border-green-100' : 'border-red-100'}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${(summary?.operating_profit || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <Wallet className={`w-4 h-4 ${(summary?.operating_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <div className="metric-label mb-0">Operating Profit</div>
          </div>
          <div className={`metric-value currency ${(summary?.operating_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(summary?.operating_profit || 0)}</div>
          <div className="text-xs text-zinc-500 mt-2">Margin: {profitMargin}%</div>
        </Card>
        <MetricCard icon={PiggyBank} iconBg="from-blue-400 to-blue-500" label="Investments" value={formatCurrency(summary?.investments_this_month || 0)} sub="Profit allocation" />
        <Card className={`roch-card metric-card h-full ${(summary?.remaining_profit || 0) >= 0 ? 'border-emerald-100' : 'border-orange-100'}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${(summary?.remaining_profit || 0) >= 0 ? 'bg-emerald-100' : 'bg-orange-100'}`}>
              <Wallet className={`w-4 h-4 ${(summary?.remaining_profit || 0) >= 0 ? 'text-emerald-600' : 'text-orange-600'}`} />
            </div>
            <div className="metric-label mb-0">Remaining Profit</div>
          </div>
          <div className={`metric-value currency ${(summary?.remaining_profit || 0) >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>{formatCurrency(summary?.remaining_profit || 0)}</div>
          <div className="text-xs text-zinc-500 mt-2">After investments</div>
        </Card>
      </div>

      {/* Monthly P&L Overview - PRIMARY CHART */}
      <div className="bento-span-12">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium flex items-center gap-2"><BarChart3 className="w-4 h-4" />Monthly P&L Overview</CardTitle></CardHeader>
          <CardContent>
            {(!chartData?.monthly_overview || chartData.monthly_overview.length === 0) ? (
              <div className="text-center py-8"><p className="text-sm text-zinc-500">No monthly data available</p></div>
            ) : (
              <div className="h-[320px]" data-testid="monthly-pnl-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.monthly_overview} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: '#71717A' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#71717A' }} tickFormatter={(v) => `₹${v >= 100000 ? (v / 100000).toFixed(1) + 'L' : (v / 1000).toFixed(0) + 'K'}`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: '10px', border: '1px solid #E4E4E7', fontSize: '13px' }} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="revenue" name="Revenue" fill="#18181B" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" name="Operating Expenses" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="operating_profit" name="Operating Profit" fill="#22C55E" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="investments" name="Investments" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend - 30 day daily */}
      <div className="bento-span-8">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium flex items-center gap-2"><BarChart3 className="w-4 h-4" />Last 30 Days Trend</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData?.daily_trend || []}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#18181B" stopOpacity={0.1}/><stop offset="95%" stopColor="#18181B" stopOpacity={0}/></linearGradient>
                    <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#EF4444" stopOpacity={0.1}/><stop offset="95%" stopColor="#EF4444" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#71717A' }} tickFormatter={(v) => new Date(v).getDate()} />
                  <YAxis tick={{ fontSize: 11, fill: '#71717A' }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={(l) => new Date(l).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} contentStyle={{ borderRadius: '10px', border: '1px solid #E4E4E7' }} />
                  <Area type="monotone" dataKey="revenue" stroke="#18181B" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" />
                  <Area type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} fillOpacity={1} fill="url(#colorExpenses)" name="Expenses" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expense Breakdown */}
      <div className="bento-span-4">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium flex items-center gap-2"><FileText className="w-4 h-4" />Expense Breakdown</CardTitle></CardHeader>
          <CardContent>
            {expenseChartData.length === 0 ? (
              <div className="text-center py-8"><p className="text-sm text-zinc-500">No expenses this month</p></div>
            ) : (
              <>
                <div className="w-[160px] h-[160px] mx-auto">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={expenseChartData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value">{expenseChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Pie><Tooltip formatter={(v) => formatCurrency(v)} /></PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 mt-3">
                  {expenseChartData.slice(0, 6).map((item, i) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} /><span className="text-zinc-600 truncate max-w-[120px]">{item.name}</span></div>
                      <span className="font-medium">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Venture */}
      <div className="bento-span-6">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Revenue by Venture</CardTitle></CardHeader>
          <CardContent>
            {ventureChartData.length === 0 ? (
              <div className="text-center py-8"><p className="text-sm text-zinc-500">No revenue this month</p></div>
            ) : (
              <div className="space-y-4">
                {ventureChartData.map((v, i) => (
                  <div key={v.name}>
                    <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-medium">{v.name}</span><span className="text-sm text-zinc-600">{formatCurrency(v.value)}</span></div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (v.value / Math.max(...ventureChartData.map(x => x.value))) * 100)}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} /></div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ========== OWNER DASHBOARD (full view) ==========
function OwnerDashboard({ summary, chartData, marginAlerts, expenseChartData, ventureChartData }) {
  const navigate = useNavigate();
  const hasPendingBankTx = (summary?.pending_bank_tx_count || 0) > 0;
  
  return (
    <div className="bento-grid stagger-children" data-testid="owner-dashboard">
      {/* Pending Bank Transactions Warning */}
      {hasPendingBankTx && (
        <div className="col-span-12">
          <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="pending-bank-warning">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-800">
                  {summary.pending_bank_tx_count} bank transactions pending confirmation
                </p>
                <p className="text-sm text-amber-600">
                  Pending expense amount: {formatCurrency(summary.pending_bank_expense_amount || 0)} — These won't appear in reports until confirmed
                </p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/exception-review')}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
            >
              Review & Confirm <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      <div className="col-span-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <MetricCard icon={TrendingUp} iconBg="from-amber-400 to-amber-500" label="Revenue" value={formatCurrency(summary?.sales_this_month || 0)} sub={<span className={summary?.sales_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}>{summary?.sales_change_percent >= 0 ? <ArrowUpRight className="w-3 h-3 inline" /> : <ArrowDownRight className="w-3 h-3 inline" />}{Math.abs(summary?.sales_change_percent || 0)}% vs last month</span>} />
        <MetricCard icon={TrendingDown} iconBg="from-zinc-100 to-zinc-200" iconColor="text-zinc-600" label="Operating Expenses" value={formatCurrency(summary?.expenses_this_month || 0)} sub="Excl. investments" />
        <Card className={`roch-card metric-card h-full ${(summary?.profit_this_month || 0) >= 0 ? 'border-green-100' : 'border-red-100'}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${(summary?.profit_this_month || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <Wallet className={`w-4 h-4 ${(summary?.profit_this_month || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <div className="metric-label mb-0">Operating Profit</div>
          </div>
          <div className={`metric-value currency ${(summary?.profit_this_month || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(summary?.profit_this_month || 0)}</div>
          <div className="text-xs text-zinc-500 mt-2">Revenue - Expenses</div>
        </Card>
        <MetricCard icon={PiggyBank} iconBg="from-blue-400 to-blue-500" label="Investments" value={formatCurrency(summary?.investments_this_month || 0)} sub="Profit allocation" />
        <Card className={`roch-card metric-card h-full ${(summary?.remaining_profit || 0) >= 0 ? 'border-emerald-100' : 'border-orange-100'}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${(summary?.remaining_profit || 0) >= 0 ? 'bg-emerald-100' : 'bg-orange-100'}`}>
              <Wallet className={`w-4 h-4 ${(summary?.remaining_profit || 0) >= 0 ? 'text-emerald-600' : 'text-orange-600'}`} />
            </div>
            <div className="metric-label mb-0">Remaining Profit</div>
          </div>
          <div className={`metric-value currency ${(summary?.remaining_profit || 0) >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>{formatCurrency(summary?.remaining_profit || 0)}</div>
          <div className="text-xs text-zinc-500 mt-2">After investments</div>
        </Card>
      </div>

      {/* Monthly P&L Overview - PRIMARY CHART */}
      <div className="bento-span-12">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium flex items-center gap-2"><BarChart3 className="w-4 h-4" />Monthly P&L Overview</CardTitle></CardHeader>
          <CardContent>
            {(!chartData?.monthly_overview || chartData.monthly_overview.length === 0) ? (
              <div className="text-center py-8"><p className="text-sm text-zinc-500">No monthly data available</p></div>
            ) : (
              <div className="h-[320px]" data-testid="owner-monthly-pnl-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.monthly_overview} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: '#71717A' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#71717A' }} tickFormatter={(v) => `₹${v >= 100000 ? (v / 100000).toFixed(1) + 'L' : (v / 1000).toFixed(0) + 'K'}`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: '10px', border: '1px solid #E4E4E7', fontSize: '13px' }} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="revenue" name="Revenue" fill="#18181B" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" name="Operating Expenses" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="operating_profit" name="Operating Profit" fill="#22C55E" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="investments" name="Investments" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend - 30 days */}
      <div className="bento-span-8">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Last 30 Days Trend</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData?.daily_trend || []}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#18181B" stopOpacity={0.1}/><stop offset="95%" stopColor="#18181B" stopOpacity={0}/></linearGradient>
                    <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#EF4444" stopOpacity={0.1}/><stop offset="95%" stopColor="#EF4444" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#71717A' }} tickFormatter={(v) => new Date(v).getDate()} />
                  <YAxis tick={{ fontSize: 11, fill: '#71717A' }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={(l) => new Date(l).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} contentStyle={{ borderRadius: '10px', border: '1px solid #E4E4E7' }} />
                  <Area type="monotone" dataKey="revenue" stroke="#18181B" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" />
                  <Area type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} fillOpacity={1} fill="url(#colorExpenses)" name="Expenses" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Margin Alerts */}
      <div className="bento-span-4">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Margin Radar</CardTitle></CardHeader>
          <CardContent>
            {marginAlerts.length === 0 ? (
              <div className="text-center py-8"><div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center mx-auto mb-3"><TrendingUp className="w-5 h-5 text-zinc-400" /></div><p className="text-sm text-zinc-500">No alerts</p><p className="text-xs text-zinc-400 mt-1">Add ingredient purchases to track margins</p></div>
            ) : (
              <div className="space-y-3">
                {marginAlerts.slice(0, 4).map((alert, i) => (
                  <div key={i} className={`alert-card ${alert.direction === 'up' || alert.type === 'cost_ratio' ? 'warning' : 'info'}`}>
                    <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${alert.direction === 'up' ? 'text-amber-500' : 'text-blue-500'}`} />
                    <div className="text-sm">{alert.type === 'price_change' ? <><span className="font-medium">{alert.ingredient}</span> price {alert.direction === 'up' ? 'increased' : 'decreased'} <span className={alert.direction === 'up' ? 'text-amber-600' : 'text-green-600'}>{Math.abs(alert.change_percent)}%</span></> : alert.message}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expense Breakdown */}
      <div className="bento-span-6">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Expense Breakdown</CardTitle></CardHeader>
          <CardContent>
            {expenseChartData.length === 0 ? (
              <div className="text-center py-8"><p className="text-sm text-zinc-500">No expenses recorded this month</p></div>
            ) : (
              <div className="flex items-center">
                <div className="w-[180px] h-[180px]">
                  <ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={expenseChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">{expenseChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Pie><Tooltip formatter={(v) => formatCurrency(v)} /></PieChart></ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2 ml-4">
                  {expenseChartData.slice(0, 5).map((item, i) => (
                    <div key={item.name} className="flex items-center justify-between text-sm"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} /><span className="text-zinc-600">{item.name}</span></div><span className="font-medium">{formatCurrency(item.value)}</span></div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Venture */}
      <div className="bento-span-6">
        <Card className="roch-card h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Revenue by Venture</CardTitle></CardHeader>
          <CardContent>
            {ventureChartData.length === 0 ? (
              <div className="text-center py-8"><p className="text-sm text-zinc-500">No revenue this month</p><p className="text-xs text-zinc-400 mt-1">Add ventures in Settings to get started</p></div>
            ) : (
              <div className="space-y-4">
                {ventureChartData.map((v, i) => (
                  <div key={v.name}><div className="flex items-center justify-between mb-1.5"><span className="text-sm font-medium">{v.name}</span><span className="text-sm text-zinc-600">{formatCurrency(v.value)}</span></div><div className="h-2 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (v.value / Math.max(...ventureChartData.map(x => x.value))) * 100)}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} /></div></div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ========== Shared MetricCard ==========
function MetricCard({ icon: Icon, iconBg, iconColor, label, value, sub, subColor }) {
  return (
    <Card className="roch-card metric-card h-full">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${iconColor || 'text-white'}`} />
        </div>
        <div className="metric-label mb-0">{label}</div>
      </div>
      <div className="metric-value currency">{value}</div>
      {sub && <div className={`text-xs mt-2 ${subColor || 'text-zinc-500'}`}>{sub}</div>}
    </Card>
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}
